import React from 'react';
import { observer } from 'mobx-react-lite';
import { ChevronLeft, Plus, X, Calendar, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import CompactDatePicker from '../ui/compact-date-picker';
import TimePickerDrawer from '../ui/time-picker-drawer';
import { useAvailabilityViewModel } from '../../view-model/professional/availability-view-model';
import { useTranslation } from 'react-i18next';

// Obtenir l'abréviation du jour selon la locale
const getDayAbbreviation = (dayIndex: number): string => {
  const date = new Date(2022, 0, 2 + dayIndex); // 2 janvier 2022 était un dimanche
  return date.toLocaleDateString('fr-FR', { weekday: 'short' }).charAt(0).toUpperCase();
};

const EditRecurringSlotsManager: React.FC = observer(() => {
  const { t } = useTranslation();
  const viewModel = useAvailabilityViewModel();

  // Vue liste des configurations
  if (!viewModel.selectedEditConfig) {
    const configs = viewModel.getGroupedConfigs();

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col h-full">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold">
            {t('availability.editRecurringSlots', 'Éditer les créneaux récurrents')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('availability.editRecurringSlotsDescription', 'Sélectionnez une configuration pour la modifier')}
          </p>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          {configs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('availability.noRecurringSlots', 'Aucun créneau récurrent')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('availability.noRecurringSlotsDescription', 'Commencez par ajouter des créneaux récurrents')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {configs.map((config) => {
                const activeDays = config.slots.filter(s => !s.isDeleted).length;
                const periodText = config.isFullYear
                  ? `${t('availability.fullYear', 'Année complète')} ${new Date().getFullYear()}`
                  : `${new Date(config.recurrenceStartDate).toLocaleDateString('fr-FR')} - ${new Date(config.recurrenceEndDate).toLocaleDateString('fr-FR')}`;

                return (
                  <div
                    key={config.recurrenceKey}
                    onClick={() => viewModel.selectEditConfig(config)}
                    className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="h-4 w-4 text-primary" />
                          <h3 className="font-medium text-gray-900 dark:text-gray-100">
                            {periodText}
                          </h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {activeDays} {activeDays > 1 ? t('availability.days', 'jours') : t('availability.day', 'jour')} {t('availability.configured', 'configuré(s)')}
                        </p>
                      </div>
                      <ChevronLeft className="h-5 w-5 text-gray-400 transform rotate-180" />
                    </div>

                    {/* Aperçu des jours */}
                    <div className="flex flex-wrap gap-1 mt-3">
                      {Array.from({ length: 7 }).map((_, index) => {
                        const dayIndex = (index + 1) % 7;
                        const hasSlotsForDay = config.slots.some(s => s.dayOfWeek === dayIndex && !s.isDeleted);
                        const dayAbbr = getDayAbbreviation(dayIndex);

                        return (
                          <div
                            key={dayIndex}
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                              hasSlotsForDay
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                            }`}
                          >
                            {dayAbbr}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Vue détail d'une configuration
  const config = viewModel.selectedEditConfig;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => viewModel.backToConfigList()}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            aria-label={t('availability.back', 'Retour') || 'Retour'}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <h2 className="text-xl font-semibold">
              {config.isFullYear
                ? `${t('availability.fullYear', 'Année complète')} ${new Date().getFullYear()}`
                : t('availability.customPeriod', 'Période personnalisée')}
            </h2>
          </div>
        </div>

        {/* Modification de la période pour les périodes personnalisées */}
        {!config.isFullYear && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <CompactDatePicker
              label={t('availability.debutDate', 'Date de début') || ''}
              id="edit-start-date"
              name="edit-start-date"
              value={viewModel.editConfigStartDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                viewModel.updateEditConfigPeriod(e.target.value, viewModel.editConfigEndDate)
              }
              min={new Date().toISOString().split('T')[0]}
              required
            />
            <CompactDatePicker
              label={t('availability.finDate', 'Date de fin') || ''}
              id="edit-end-date"
              name="edit-end-date"
              value={viewModel.editConfigEndDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                viewModel.updateEditConfigPeriod(viewModel.editConfigStartDate, e.target.value)
              }
              min={viewModel.editConfigStartDate}
              required
            />
          </div>
        )}
      </div>

      {/* Liste des jours */}
      <div className="p-4 flex-1 overflow-y-auto">
        <div className="space-y-6">
          {Array.from({ length: 7 }).map((_, index) => {
            const dayIndex = (index + 1) % 7; // Lundi à dimanche
            const daySlots = config.slots.filter(s => s.dayOfWeek === dayIndex && !s.isDeleted);
            const dayAbbreviation = getDayAbbreviation(dayIndex);

            return (
              <div key={dayIndex} className="mb-4">
                {daySlots.length === 0 ? (
                  <div className="flex items-center w-full">
                    {/* Cercle avec initiale du jour */}
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-medium text-sm">
                        {dayAbbreviation}
                      </div>
                    </div>

                    {/* Jour sans créneaux */}
                    <div className="flex-grow ml-3 flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400 font-medium text-lg">
                        {t('availability.unavailable', 'Indisponible')}
                      </span>
                      <button
                        onClick={() => viewModel.addEditSlotToDay(dayIndex)}
                        className="ml-2 w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300"
                        aria-label={t('availability.addSlot', 'Ajouter un créneau') || 'Ajouter un créneau'}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Jour avec créneaux */
                  <div className="flex flex-col">
                    {daySlots.map((slot, slotIndex) => (
                      <div key={`${dayIndex}-${slotIndex}`} className="flex items-start mb-2 last:mb-0">
                        {/* Cercle avec initiale du jour (seulement pour le premier créneau) */}
                        {slotIndex === 0 && (
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-medium text-sm">
                              {dayAbbreviation}
                            </div>
                          </div>
                        )}

                        {/* Espace pour aligner les créneaux suivants */}
                        {slotIndex > 0 && <div className="w-8 flex-shrink-0"></div>}

                        {/* Contenu du créneau */}
                        <div className="flex-grow ml-3 flex items-center justify-between">
                          {/* Horaires */}
                          <div className="flex flex-col sm:flex-row gap-2 min-w-[400px]">
                            <TimePickerDrawer
                              id={`edit-start-time-${dayIndex}-${slotIndex}`}
                              name="start-time"
                              value={slot.startTime}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newStartTime = e.target.value;
                                viewModel.updateEditSlotTime(dayIndex, slotIndex, 'startTime', newStartTime);

                                // Calculer le nouveau minimum pour l'heure de fin
                                const minEndTime = viewModel.calculateMinEndTime(newStartTime);
                                if (slot.endTime < minEndTime) {
                                  viewModel.updateEditSlotTime(dayIndex, slotIndex, 'endTime', minEndTime);
                                }
                              }}
                              step={1800}
                              required
                            />

                            <TimePickerDrawer
                              id={`edit-end-time-${dayIndex}-${slotIndex}`}
                              name="end-time"
                              value={slot.endTime}
                              minTime={viewModel.calculateMinEndTime(slot.startTime)}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                viewModel.updateEditSlotTime(dayIndex, slotIndex, 'endTime', e.target.value);
                              }}
                              step={1800}
                              required
                              withButton={false}
                            />
                          </div>

                          {/* Groupe des boutons */}
                          <div
                            className="flex items-center space-x-1"
                            style={{ width: '72px', justifyContent: slotIndex === 0 ? 'flex-start' : 'flex-end' }}
                          >
                            {/* Bouton ajouter (seulement pour le premier créneau) */}
                            {slotIndex === 0 && (
                              <button
                                onClick={() => viewModel.addEditSlotToDay(dayIndex)}
                                className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300"
                                aria-label={t('availability.addSlot', 'Ajouter un créneau') || 'Ajouter un créneau'}
                              >
                                <Plus size={14} />
                              </button>
                            )}

                            {/* Bouton supprimer (pour tous les créneaux) */}
                            <button
                              onClick={() => viewModel.markEditSlotDeleted(dayIndex, slotIndex)}
                              className="w-6 h-6 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                              aria-label={t('availability.deleteSlot', 'Supprimer le créneau') || 'Supprimer le créneau'}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer avec boutons d'action */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 mt-auto">
        <div className="flex flex-col sm:flex-row gap-2 sm:justify-between">
          {/* Bouton supprimer la configuration */}
          <Button
            onClick={() => viewModel.handleDeleteConfig()}
            variant="secondary"
            className="w-full sm:w-auto flex items-center justify-center bg-red-600 hover:bg-red-700 text-white"
            disabled={viewModel.isLoading}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t('availability.deleteConfig', 'Supprimer la configuration')}
          </Button>

          {/* Bouton enregistrer */}
          <Button
            onClick={() => viewModel.handleSaveConfig()}
            className="w-full sm:w-auto py-3 sm:py-2 px-4 flex items-center justify-center text-base sm:text-sm"
            disabled={viewModel.isLoading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            {viewModel.isLoading ? t('availability.saving', 'Enregistrement...') : t('availability.save', 'Enregistrer')}
          </Button>
        </div>
      </div>
    </div>
  );
});

export default EditRecurringSlotsManager;
